import React, { useRef, useState } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import { BsWhatsapp } from "react-icons/bs";
import axios from "axios";
import { Spinner } from "react-bootstrap";
import { useSelector } from "react-redux";

import { sendMessageWhatsApp, getClassStudents } from "../../../utils/class";
import { ErrorMsg, success } from "../../../utils/Toast";
import { HomePage } from "../../../translations/trans";
import { urlBase } from "../../../utils/API";

const SendMsgToClass = ({ Class }) => {
  const [message, setMessage] = useState("");
  const [type, setType] = useState("");
  const [show, setShow] = useState(false);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  // For specific student selection
  const [isAll, setIsAll] = useState(true);
  const [studentsList, setStudentsList] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);

  const fileInputRef = useRef(null);

  const loggedUser = useSelector((state) => state.loggedUser);
  const { lang, dir } = useSelector(({ language }) => ({
    lang: language.lang,
    dir: language.dir,
  }));

  // Upload file locally
  const uploadingFileLocally = (data) => {
    const formData = new FormData();
    formData.append("file", data);
    return axios({
      method: "POST",
      url: `${urlBase}/api/upload-file-locally?userId=${loggedUser?.user?._id}`,
      data: formData,
    });
  };

  // Fetch students when "specific students" selected
  const fetchStudentsIfNeeded = async () => {
    if (studentsList.length === 0) {
      setLoadingStudents(true);
      try {
        const res = await getClassStudents(Class?._id, loggedUser?.user?._id);
        const students = res?.data || res;
        if (Array.isArray(students)) setStudentsList(students);
      } catch (error) {
        console.error("Error fetching students:", error);
        ErrorMsg("حدث خطأ أثناء جلب قائمة الطلاب");
      } finally {
        setLoadingStudents(false);
      }
    }
  };

  // Switch between sending to all / specific
  const handleIsAllChange = (e) => {
    const newValue = e.target.value === "true";
    setIsAll(newValue);
    if (!newValue) fetchStudentsIfNeeded();
  };

  // Toggle one student
  const handleStudentToggle = (studentId) => {
    setSelectedStudents((prev) =>
      prev.includes(studentId)
        ? prev.filter((id) => id !== studentId)
        : [...prev, studentId]
    );
  };

  // Select all / unselect all
  const handleSelectAllToggle = (e) => {
    if (e.target.checked) {
      setSelectedStudents(studentsList.map((s) => s._id));
    } else {
      setSelectedStudents([]);
    }
  };

  const handleShow = () => setShow(true);

  const handleClose = () => {
    setShow(false);
    setMessage("");
    setFile(null);
    setIsAll(true);
    setSelectedStudents([]);
    if (fileInputRef.current) fileInputRef.current.value = ""; // ✅ Reset actual input
  };

  const handleAddMessageWhats = async () => {
    if (!type) {
      ErrorMsg("من فضلك اختر نوع مستقبل الرسالة (ولي أمر/طالب)");
      return;
    }
    if (message === "" && !file) {
      ErrorMsg("الرسالة فارغة، يجب إضافة نص أو ملف");
      return;
    }
    if (!isAll && selectedStudents.length === 0) {
      ErrorMsg("يجب تحديد طالب واحد على الأقل عند اختيار 'تحديد طلاب'");
      return;
    }

    setLoading(true);
    try {
      let fileUrl = null;
      if (file) {
        const uploadResponse = await uploadingFileLocally(file);
        fileUrl = uploadResponse.data.data.name;
      }

      const res = await sendMessageWhatsApp(
        {
          classId: Class?._id,
          message,
          type,
          isAll,
          students: isAll ? [] : selectedStudents,
          file: fileUrl,
        },
        loggedUser?.user?._id
      );

      if (res?.data?.case) {
        success(res?.data?.message);
        handleClose();
      }
    } catch (e) {
      console.error(e);
      ErrorMsg(e?.response?.data?.message || "حدث خطأ أثناء الإرسال");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <BsWhatsapp
        color={"#25D366"}
        size={"25"}
        style={{ cursor: "pointer" }}
        onClick={handleShow}
      />

      <Modal show={show} onHide={handleClose} centered dir={dir} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{HomePage.enterMessage[lang]}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {/* Send to all or specific */}
          <div className="mb-3">
            <Form.Check
              inline
              label="إرسال للجميع"
              name="isAllGroup"
              type="radio"
              id="sendToAll"
              value={true}
              checked={isAll === true}
              onChange={handleIsAllChange}
            />
            <Form.Check
              inline
              label="تحديد طلاب"
              name="isAllGroup"
              type="radio"
              id="sendToSpecific"
              value={false}
              checked={isAll === false}
              onChange={handleIsAllChange}
            />
          </div>

          {/* Student list */}
          {!isAll && (
            <div
              className="border p-2 mb-3 rounded"
              style={{
                maxHeight: "200px",
                overflowY: "auto",
                backgroundColor: "#f8f9fa",
              }}
            >
              {loadingStudents ? (
                <div className="text-center">
                  <Spinner animation="border" size="sm" /> جاري تحميل الطلاب...
                </div>
              ) : studentsList.length > 0 ? (
                <>
                  <Form.Check
                    type="checkbox"
                    label={
                      <strong
                        style={{
                          marginRight: "5px",
                          marginLeft: "5px",
                        }}
                      >
                        تحديد الكل
                      </strong>
                    }
                    onChange={handleSelectAllToggle}
                    checked={
                      selectedStudents.length === studentsList.length &&
                      studentsList.length > 0
                    }
                    className="mb-2 pb-2 border-bottom"
                  />
                  {studentsList.map((student) => (
                    <Form.Check
                      key={student._id}
                      type="checkbox"
                      id={`student-${student._id}`}
                      label={student.name}
                      checked={selectedStudents.includes(student._id)}
                      onChange={() => handleStudentToggle(student._id)}
                      style={{
                        marginRight: dir === "rtl" ? "0" : "1.5em",
                        marginLeft: dir === "rtl" ? "1.5em" : "0",
                      }}
                    />
                  ))}
                </>
              ) : (
                <div className="text-danger">لا يوجد طلاب في هذا الفصل</div>
              )}
            </div>
          )}

          {/* Message Type */}
          <Form.Group className="mb-3">
            <Form.Label>{HomePage.chooseType[lang]}</Form.Label>
            <Form.Select
              onChange={(e) => setType(e.target.value)}
              value={type}
            >
              <option value="" disabled hidden>
                {HomePage.chooseType[lang]}
              </option>
              <option value="1">{HomePage.sendToStudent[lang]}</option>
              <option value="2">{HomePage.sendToParent[lang]}</option>
              <option value="3">{HomePage.sendToParentAndStudent[lang]}</option>
            </Form.Select>
          </Form.Group>

          {/* File Upload */}
         {/* File Upload */}
       <input
                        type="file"
                        className='form-control'
                        accept="image/*,.pdf,.doc,.docx"
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => setFile(e.target.files[0])}
                    />


          {/* Message text */}
          <Form.Group className="mb-3">
            <Form.Control
              as="textarea"
              rows={5}
              placeholder={HomePage.enterMessage[lang]}
              style={{ resize: "none" }}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === " ") e.stopPropagation();
              }}
            />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            {HomePage.cancel[lang]}
          </Button>
          <Button
            variant="success"
            onClick={handleAddMessageWhats}
            disabled={loading || (!isAll && loadingStudents)}
          >
            {loading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />{" "}
                جاري الإرسال...
              </>
            ) : (
              HomePage.send[lang]
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SendMsgToClass;
